/**
 * `useLatestRef` — the value as of the latest COMMITTED render, readable
 * from a closure that kept an older one. Task 306A §1.5.1 / §1.5.2.
 *
 * ---------------------------------------------------------------------
 * WHY IT EXISTS.
 *
 * `PlatformCommercialPage`'s `runAction` is rebuilt on every render, and
 * the promise chain it starts keeps the closure it was called with. Every
 * value that chain reads AFTER its `await` is therefore the value from the
 * render the operator pressed the button in. For the state filter that is
 * actively dangerous once the list read carries a generation token — the
 * stale-filter re-read is issued last, takes the newest token, and becomes
 * the only request allowed to write. For the detail map it silently skips
 * the re-read of a row opened mid-transition.
 *
 * ---------------------------------------------------------------------
 * WHY IT IS A LAYOUT EFFECT AND NOT A RENDER-BODY ASSIGNMENT.
 *
 * The first version of this hook wrote `ref.current = value` in the render
 * body. That publishes a value React has not committed. Under concurrent
 * rendering React may START a render, run the component body — writing the
 * ref — and then interrupt or discard that render without ever committing
 * it. A promise settling in that window reads a filter the operator has
 * never seen, and hands it the newest generation token. Review round 22
 * found it (§9.3); `StrictMode` does not make it safe, it only exercises
 * rendering harder.
 *
 * `useLayoutEffect` runs synchronously AFTER the commit and before paint,
 * so what it publishes is exactly what is on screen, and a render that is
 * thrown away publishes nothing. A passive `useEffect` would be worse than
 * either: it is deferred, so the ref would lag a filter change that HAS
 * committed, and the re-read would go out under the previous filter.
 *
 * ---------------------------------------------------------------------
 * WHY IT IS ITS OWN MODULE.
 *
 * So the publication timing is EXECUTED rather than pinned as a source
 * string. `useLatestRef.306a.test.ts` mocks `react` and drives render →
 * commit → effect-flush cycles directly, including a render that is
 * started and discarded. That is only cheap because this module imports
 * two hooks and nothing else; the page it serves imports the API client,
 * the session context and the state components, and mocking `react` for
 * that module graph would be a harness, not a test.
 */

import { useLayoutEffect, useRef } from 'react';

export function useLatestRef<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}
